import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Grid, Alert, Card, CardContent, Tabs, Tab } from '@mui/material';
import { Icon } from '@iconify/react';
import { EditIcon, XIcon, SendIcon, EyeIcon } from 'lucide-react';
import { createAuditLog } from '../../utils/auditLog';
interface Transaction {
  id: string;
  amount: number;
  currency: string;
  payoutAmount: number;
  payoutCurrency: string;
  senderName: string;
  receiverName: string;
  status: string;
  checkerDecision?: string;
  checkerReason?: string;
  rejectionReason?: string;
  rejectedAt?: string;
  rejectedBy?: string;
  createdAt: string;
  parties?: any;
  accounts?: any;
  overview?: any;
}
const MakerApprovalHistoryPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [rejectedTransactions, setRejectedTransactions] = useState<Transaction[]>([]);
  const [allTransactions, setAllTransactions] = useState<Transaction[]>([]);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [editedData, setEditedData] = useState<any>({});
  useEffect(() => {
    loadTransactions();
    const handleUpdate = () => {
      loadTransactions();
    };
    window.addEventListener('transactionUpdated', handleUpdate);
    window.addEventListener('transactionAdded', handleUpdate);
    return () => {
      window.removeEventListener('transactionUpdated', handleUpdate);
      window.removeEventListener('transactionAdded', handleUpdate);
    };
  }, []);
  const loadTransactions = () => {
    const stored = localStorage.getItem('initiatedTransactions');
    if (stored) {
      const transactions = JSON.parse(stored);
      // Filter rejected transactions
      const rejected = transactions.filter((t: Transaction) => t.status === 'Rejected' || t.checkerDecision === 'rejected');
      setRejectedTransactions(rejected);
      // All transactions for history
      setAllTransactions(transactions);
    }
  };
  const handleEdit = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setEditedData({
      amount: transaction.amount,
      receiverName: transaction.receiverName,
      senderName: transaction.senderName,
      payoutAmount: transaction.payoutAmount
    });
    setShowEditDialog(true);
  };
  const handleView = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setShowViewDialog(true);
  };
  const handleResubmit = () => {
    if (!selectedTransaction) return;
    const stored = localStorage.getItem('initiatedTransactions');
    if (!stored) return;
    const allTransactions = JSON.parse(stored);
    const updatedTransactions = allTransactions.map((t: any) => {
      if (t.id === selectedTransaction.id) {
        return {
          ...t,
          ...editedData,
          status: 'Pending Checker Approval',
          checkerDecision: null,
          checkerReason: null,
          rejectionReason: null,
          rejectedAt: null,
          rejectedBy: null,
          resubmittedAt: new Date().toISOString(),
          overview: {
            ...t.overview,
            status: 'Pending Checker Approval'
          }
        };
      }
      return t;
    });
    localStorage.setItem('initiatedTransactions', JSON.stringify(updatedTransactions));
    createAuditLog({
      userId: selectedTransaction.id,
      eventType: 'Edit',
      source: 'BO_USER',
      changes: [{
        fieldName: 'Transaction Status',
        oldValue: 'Rejected',
        newValue: 'Pending Checker Approval (Resubmitted)'
      }]
    });
    window.dispatchEvent(new Event('transactionUpdated'));
    setShowEditDialog(false);
    loadTransactions();
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Completed':
        return 'success';
      case 'Rejected':
        return 'error';
      case 'Processing':
      case 'Pending Checker Approval':
        return 'warning';
      default:
        return 'default';
    }
  };
  const displayedTransactions = activeTab === 0 ? rejectedTransactions : allTransactions;
  return <Box sx={{
    p: 3
  }} data-id="element-875">
      <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      mb: 3
    }} data-id="element-876">
        <Icon icon="lucide:history" width={28} height={28} color="#0052CC" data-id="element-877" />
        <Typography variant="h4" sx={{
        fontWeight: 700,
        color: '#0052CC'
      }} data-id="element-878">
          Maker Approval History
        </Typography>
      </Box>

      <Paper elevation={0} sx={{
      borderRadius: 3,
      mb: 3
    }} data-id="element-879">
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{
        borderBottom: 1,
        borderColor: 'divider',
        '& .MuiTab-root': {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '1rem'
        }
      }} data-id="element-880">
          <Tab label={<Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} data-id="element-882">
                Rejected Transactions
                {rejectedTransactions.length > 0 && <Chip label={rejectedTransactions.length} size="small" color="error" sx={{
            height: 20,
            fontSize: '0.75rem'
          }} data-id="element-883" />}
              </Box>} data-id="element-881" />
          <Tab label="All Transactions" data-id="element-884" />
        </Tabs>
      </Paper>

      {activeTab === 0 && rejectedTransactions.length > 0 && <Alert severity="warning" sx={{
      mb: 3
    }} data-id="element-885">
          You have {rejectedTransactions.length} rejected transaction
          {rejectedTransactions.length !== 1 ? 's' : ''} that need attention.
          Review and resubmit them for approval.
        </Alert>}

      <TableContainer component={Paper} elevation={0} sx={{
      borderRadius: 3
    }} data-id="element-886">
        <Table data-id="element-887">
          <TableHead data-id="element-888">
            <TableRow sx={{
            bgcolor: activeTab === 0 ? 'error.50' : 'primary.50'
          }} data-id="element-889">
              <TableCell sx={{
              fontWeight: 600
            }} data-id="element-890">
                Transaction ID
              </TableCell>
              <TableCell sx={{
              fontWeight: 600
            }} data-id="element-891">
                Sender
              </TableCell>
              <TableCell sx={{
              fontWeight: 600
            }} data-id="element-892">
                Receiver
              </TableCell>
              <TableCell sx={{
              fontWeight: 600
            }} data-id="element-893">
                Amount
              </TableCell>
              <TableCell sx={{
              fontWeight: 600
            }} data-id="element-894">
                Status
              </TableCell>
              <TableCell sx={{
              fontWeight: 600
            }} data-id="element-895">
                Created
              </TableCell>
              {activeTab === 0 && <TableCell sx={{
              fontWeight: 600
            }} data-id="element-896">
                  Rejection Reason
                </TableCell>}
              <TableCell sx={{
              fontWeight: 600
            }} data-id="element-897">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody data-id="element-898">
            {displayedTransactions.length === 0 ? <TableRow data-id="element-899">
                <TableCell colSpan={activeTab === 0 ? 8 : 7} align="center" sx={{
              py: 4
            }} data-id="element-900">
                  <Typography color="text.secondary" data-id="element-901">
                    {activeTab === 0 ? 'No rejected transactions found' : 'No transactions found'}
                  </Typography>
                </TableCell>
              </TableRow> : displayedTransactions.map(transaction => <TableRow key={transaction.id} hover data-id="element-902">
                  <TableCell data-id="element-903">{transaction.id}</TableCell>
                  <TableCell data-id="element-904">{transaction.senderName}</TableCell>
                  <TableCell data-id="element-905">{transaction.receiverName}</TableCell>
                  <TableCell data-id="element-906">
                    {transaction.amount.toLocaleString()} {transaction.currency}
                  </TableCell>
                  <TableCell data-id="element-907">
                    <Chip label={transaction.status} size="small" color={getStatusColor(transaction.status) as any} data-id="element-908" />
                  </TableCell>
                  <TableCell data-id="element-909">
                    {new Date(transaction.createdAt).toLocaleDateString()}
                  </TableCell>
                  {activeTab === 0 && <TableCell data-id="element-910">
                      <Typography variant="body2" color="error" sx={{
                maxWidth: 200
              }} data-id="element-911">
                        {transaction.rejectionReason || transaction.checkerReason}
                      </Typography>
                    </TableCell>}
                  <TableCell data-id="element-912">
                    <Box sx={{
                display: 'flex',
                gap: 0.5
              }} data-id="element-913">
                      <IconButton size="small" onClick={() => handleView(transaction)} sx={{
                  color: '#0052CC'
                }} data-id="element-914">
                        <EyeIcon size={18} data-id="element-915" />
                      </IconButton>
                      {activeTab === 0 && <IconButton size="small" onClick={() => handleEdit(transaction)} sx={{
                  color: '#0052CC'
                }} data-id="element-916">
                          <EditIcon size={18} data-id="element-917" />
                        </IconButton>}
                    </Box>
                  </TableCell>
                </TableRow>)}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit & Resubmit Dialog */}
      <Dialog open={showEditDialog} onClose={() => setShowEditDialog(false)} maxWidth="md" fullWidth data-id="element-918">
        <DialogTitle data-id="element-919">
          <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }} data-id="element-920">
            <Typography variant="h6" data-id="element-921">Edit & Resubmit Transaction</Typography>
            <IconButton onClick={() => setShowEditDialog(false)} data-id="element-922">
              <XIcon size={20} data-id="element-923" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent data-id="element-924">
          {selectedTransaction && <>
              <Alert severity="error" sx={{
            mb: 3
          }} data-id="element-925">
                <Typography variant="body2" fontWeight={600} data-id="element-926">
                  Rejection Reason:
                </Typography>
                <Typography variant="body2" data-id="element-927">
                  {selectedTransaction.rejectionReason || selectedTransaction.checkerReason}
                </Typography>
              </Alert>

              <Grid container spacing={2} data-id="element-928">
                <Grid item xs={12} md={6} data-id="element-929">
                  <TextField fullWidth label="Sender Name" value={editedData.senderName} onChange={e => setEditedData({
                ...editedData,
                senderName: e.target.value
              })} data-id="element-930" />
                </Grid>
                <Grid item xs={12} md={6} data-id="element-931">
                  <TextField fullWidth label="Receiver Name" value={editedData.receiverName} onChange={e => setEditedData({
                ...editedData,
                receiverName: e.target.value
              })} data-id="element-932" />
                </Grid>
                <Grid item xs={12} md={6} data-id="element-933">
                  <TextField fullWidth label="Send Amount" type="number" value={editedData.amount} onChange={e => setEditedData({
                ...editedData,
                amount: parseFloat(e.target.value)
              })} data-id="element-934" />
                </Grid>
                <Grid item xs={12} md={6} data-id="element-935">
                  <TextField fullWidth label="Payout Amount" type="number" value={editedData.payoutAmount} onChange={e => setEditedData({
                ...editedData,
                payoutAmount: parseFloat(e.target.value)
              })} data-id="element-936" />
                </Grid>
              </Grid>
            </>}
        </DialogContent>
        <DialogActions data-id="element-937">
          <Button onClick={() => setShowEditDialog(false)} data-id="element-938">Cancel</Button>
          <Button variant="contained" startIcon={<SendIcon size={18} data-id="element-940" />} onClick={handleResubmit} data-id="element-939">
            Resubmit for Approval
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog open={showViewDialog} onClose={() => setShowViewDialog(false)} maxWidth="lg" fullWidth data-id="element-941">
        <DialogTitle data-id="element-942">
          <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }} data-id="element-943">
            <Typography variant="h6" data-id="element-944">Transaction Details</Typography>
            <IconButton onClick={() => setShowViewDialog(false)} data-id="element-945">
              <XIcon size={20} data-id="element-946" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent data-id="element-947">
          {selectedTransaction && <Grid container spacing={3} data-id="element-948">
              <Grid item xs={12} md={6} data-id="element-949">
                <Card elevation={0} sx={{
              border: '2px solid #0052CC',
              borderRadius: 2
            }} data-id="element-950">
                  <CardContent data-id="element-951">
                    <Typography variant="h6" sx={{
                  mb: 2,
                  color: '#0052CC',
                  fontWeight: 600
                }} data-id="element-952">
                      Transaction Overview
                    </Typography>
                    <Grid container spacing={2} data-id="element-953">
                      <Grid item xs={12} data-id="element-954">
                        <Typography variant="caption" color="text.secondary" data-id="element-955">
                          Transaction ID
                        </Typography>
                        <Typography variant="body2" fontWeight={500} data-id="element-956">
                          {selectedTransaction.id}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} data-id="element-957">
                        <Typography variant="caption" color="text.secondary" data-id="element-958">
                          Status
                        </Typography>
                        <Box sx={{
                      mt: 0.5
                    }} data-id="element-959">
                          <Chip label={selectedTransaction.status} size="small" color={getStatusColor(selectedTransaction.status) as any} data-id="element-960" />
                        </Box>
                      </Grid>
                      <Grid item xs={12} data-id="element-961">
                        <Typography variant="caption" color="text.secondary" data-id="element-962">
                          Send Amount
                        </Typography>
                        <Typography variant="body2" fontWeight={500} data-id="element-963">
                          {selectedTransaction.amount.toLocaleString()}{' '}
                          {selectedTransaction.currency}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} data-id="element-964">
                        <Typography variant="caption" color="text.secondary" data-id="element-965">
                          Payout Amount
                        </Typography>
                        <Typography variant="body2" fontWeight={500} data-id="element-966">
                          {selectedTransaction.payoutAmount.toLocaleString()}{' '}
                          {selectedTransaction.payoutCurrency}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} data-id="element-967">
                        <Typography variant="caption" color="text.secondary" data-id="element-968">
                          Created At
                        </Typography>
                        <Typography variant="body2" fontWeight={500} data-id="element-969">
                          {new Date(selectedTransaction.createdAt).toLocaleString()}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6} data-id="element-970">
                <Card elevation={0} sx={{
              border: '2px solid #0052CC',
              borderRadius: 2
            }} data-id="element-971">
                  <CardContent data-id="element-972">
                    <Typography variant="h6" sx={{
                  mb: 2,
                  color: '#0052CC',
                  fontWeight: 600
                }} data-id="element-973">
                      Parties
                    </Typography>
                    <Grid container spacing={2} data-id="element-974">
                      <Grid item xs={12} data-id="element-975">
                        <Typography variant="caption" color="text.secondary" data-id="element-976">
                          Sender Name
                        </Typography>
                        <Typography variant="body2" fontWeight={500} data-id="element-977">
                          {selectedTransaction.senderName}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} data-id="element-978">
                        <Typography variant="caption" color="text.secondary" data-id="element-979">
                          Receiver Name
                        </Typography>
                        <Typography variant="body2" fontWeight={500} data-id="element-980">
                          {selectedTransaction.receiverName}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {selectedTransaction.status === 'Rejected' && <Grid item xs={12} data-id="element-981">
                  <Alert severity="error" data-id="element-982">
                    <Typography variant="body2" fontWeight={600} data-id="element-983">
                      Rejection Reason:
                    </Typography>
                    <Typography variant="body2" data-id="element-984">
                      {selectedTransaction.rejectionReason || selectedTransaction.checkerReason}
                    </Typography>
                    {selectedTransaction.rejectedAt && <Typography variant="caption" color="text.secondary" sx={{
                mt: 1,
                display: 'block'
              }} data-id="element-985">
                        Rejected on{' '}
                        {new Date(selectedTransaction.rejectedAt).toLocaleString()}
                      </Typography>}
                  </Alert>
                </Grid>}
            </Grid>}
        </DialogContent>
        <DialogActions data-id="element-986">
          <Button onClick={() => setShowViewDialog(false)} data-id="element-987">Close</Button>
        </DialogActions>
      </Dialog>
    </Box>;
};
export default MakerApprovalHistoryPage;